import { useMemo, useCallback, memo } from 'react';
import { Empty, Space, Tag } from 'antd';
import { HomeOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { PropertyData } from '@/services/scraper.service';
import { StreetGroup, PropertyGroup, groupPropertiesByStreet } from './propertyGrouping';
import { StreetGroupCard } from './StreetGroupCard';
import './PropertyGroupList.css';

interface PropertyGroupListProps {
  properties: PropertyData[];
  selectedProperties: Set<string>;
  onPropertyToggle: (property: PropertyData) => void;
  disabled?: boolean;
}

function PropertyGroupListComponent({
  properties,
  selectedProperties,
  onPropertyToggle,
  disabled = false,
}: PropertyGroupListProps) {
  const streetGroups = useMemo(
    () => groupPropertiesByStreet(properties, selectedProperties),
    [properties, selectedProperties]
  );

  const stats = useMemo(() => {
    let totalCondos = 0;
    let totalCondoProperties = 0;
    let totalLooseProperties = 0;

    streetGroups.forEach(street => {
      totalCondos += street.condominiums.length;
      street.condominiums.forEach(condo => {
        totalCondoProperties += condo.totalCount;
      });
      totalLooseProperties += street.looseProperties.length;
    });

    return {
      totalStreets: streetGroups.length,
      totalCondos,
      totalCondoProperties,
      totalLooseProperties,
    };
  }, [streetGroups]);

  const handleSelectAllInStreet = useCallback((street: StreetGroup, select: boolean) => {
    street.condominiums.forEach(condo => {
      condo.properties.forEach((property) => {
        const isSelected = selectedProperties.has(property.url);
        if ((select && !isSelected) || (!select && isSelected)) {
          onPropertyToggle(property);
        }
      });
    });

    street.looseProperties.forEach((property) => {
      const isSelected = selectedProperties.has(property.url);
      if ((select && !isSelected) || (!select && isSelected)) {
        onPropertyToggle(property);
      }
    });
  }, [selectedProperties, onPropertyToggle]);

  const handleSelectAllInCondo = useCallback((condo: PropertyGroup, select: boolean) => {
    condo.properties.forEach((property) => {
      const isSelected = selectedProperties.has(property.url);
      if ((select && !isSelected) || (!select && isSelected)) {
        onPropertyToggle(property);
      }
    });
  }, [selectedProperties, onPropertyToggle]);

  if (properties.length === 0) {
    return (
      <Empty
        description="Nenhum imovel encontrado"
        style={{ padding: '40px 0' }}
      />
    );
  }

  return (
    <div className="property-group-list">
      <div className="group-list-header">
        <Space size="middle">
          <Tag icon={<EnvironmentOutlined />} color="blue">
            {stats.totalStreets} {stats.totalStreets === 1 ? 'rua' : 'ruas'}
          </Tag>
          {stats.totalCondos > 0 && (
            <Tag icon={<HomeOutlined />} color="purple">
              {stats.totalCondos} {stats.totalCondos === 1 ? 'condominio' : 'condominios'}
            </Tag>
          )}
          <span style={{ color: '#666', fontSize: 12 }}>
            {stats.totalCondoProperties > 0 && `${stats.totalCondoProperties} em condominios`}
            {stats.totalCondoProperties > 0 && stats.totalLooseProperties > 0 && ' | '}
            {stats.totalLooseProperties > 0 && `${stats.totalLooseProperties} avulsos`}
          </span>
        </Space>
      </div>

      <div className="group-list-content">
        {streetGroups.map((street) => (
          <StreetGroupCard
            key={street.id}
            group={street}
            selectedProperties={selectedProperties}
            onPropertyToggle={onPropertyToggle}
            onSelectAllInStreet={handleSelectAllInStreet}
            onSelectAllInCondo={handleSelectAllInCondo}
            disabled={disabled}
            defaultExpanded={streetGroups.length <= 3}
          />
        ))}
      </div>

    </div>
  );
}

export const PropertyGroupList = memo(PropertyGroupListComponent);
